import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import Typography from '../Typography'
import Paper from '../Paper'
import type { ApiKeyDocument } from 'paintscout'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import DeleteIcon from '@material-ui/icons/Delete'
import ActionButton from '../ActionButton'
import { useSnackbar } from 'notistack'
import classNames from 'classnames'

const useStyles = makeStyles<Theme, ApiKeyProps>(
  (theme) => ({
    root: {
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(4)
    },
    ownerName: {
      fontWeight: 500,
      marginBottom: theme.spacing(0.5)
    },
    meta: {
      color: theme.palette.text.secondary
    },
    deactivated: {
      color: theme.palette.error.main,
      fontWeight: 400
    },
    deleteButton: {
      background: 'none',
      border: 'none',
      padding: theme.spacing(0.5),
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      alignItems: 'center',
      transition: 'color 0.15s ease',
      '&:hover': {
        color: theme.palette.error.main
      }
    },
    keyRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    keyDisplay: {
      flex: 1,
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: `${theme.spacing(0.75)}px ${theme.spacing(1.25)}px`,
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    iconButton: {
      background: 'none',
      border: 'none',
      padding: theme.spacing(0.75),
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      alignItems: 'center',
      transition: 'color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.grey[100]
      }
    },
    copyWrapper: {
      position: 'relative'
    },
    copiedTooltip: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: theme.spacing(0.5),
      backgroundColor: theme.palette.grey[900],
      color: '#fff',
      fontSize: '0.7rem',
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      borderRadius: theme.shape.borderRadius,
      whiteSpace: 'nowrap',
      pointerEvents: 'none'
    }
  }),
  { name: 'ClientApiKey' }
)

export interface ApiKeyAction {
  key: string
  label: string
  scary?: boolean
}

export interface ApiKeyProps {
  classes?: StyleClasses<typeof useStyles>
  actions?: ApiKeyAction[]
  onAction?: (key: string) => void
  apiKey: ApiKeyDocument
}

const maskKey = (key: string) => {
  return key.substring(0, 12) + '••••••••••••••••••••' + key.substring(key.length - 4)
}

function ApiKey(props: ApiKeyProps) {
  const classes = useStyles(props)
  const { apiKey, actions, onAction } = props
  const [visible, setVisible] = useState(false)
  const [copied, setCopied] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const ownerName =
    apiKey?.owner?.firstName || apiKey?.owner?.lastName
      ? `${apiKey?.owner?.firstName ?? ''} ${apiKey?.owner?.lastName ?? ''}`.trim()
      : 'No Owner'

  const createdDate = apiKey.created ? new Date(parseInt(apiKey.created)).toLocaleDateString() : '—'

  const deactivateAction = actions?.find((a) => a.key === 'deactivate')
  const useMenu = (actions?.length ?? 0) > 1

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey._id)
    enqueueSnackbar('Key Copied', { variant: 'success' })
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.ownerName} variant="body1">
            {apiKey.name || 'Unnamed API Key'}
            {apiKey.archived && <span className={classes.deactivated}> - Deactivated</span>}
          </Typography>
          <Typography className={classes.meta} variant="caption">
            Created: {createdDate} • Created by: {ownerName}
          </Typography>
        </div>
        {!apiKey.archived && onAction && useMenu && (
          <ActionButton actions={actions} onActionClick={(_, key) => onAction(key)} />
        )}
        {!apiKey.archived && deactivateAction && onAction && !useMenu && (
          <button
            className={classes.deleteButton}
            onClick={() => onAction(deactivateAction.key)}
            title={deactivateAction.label}
          >
            <DeleteIcon fontSize="small" />
          </button>
        )}
      </div>

      <div className={classes.keyRow}>
        <div className={classes.keyDisplay}>{visible ? apiKey._id : maskKey(apiKey._id)}</div>
        <button
          className={classes.iconButton}
          onClick={() => setVisible((v) => !v)}
          title={visible ? 'Hide key' : 'Show key'}
        >
          {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
        </button>
        <div className={classNames(classes.copyWrapper)}>
          <button className={classes.iconButton} onClick={handleCopy} title="Copy key">
            <FileCopyIcon fontSize="small" />
          </button>
          {copied && <span className={classes.copiedTooltip}>Copied!</span>}
        </div>
      </div>
    </Paper>
  )
}

export default ApiKey
